import i18n from '@/shared/i18n';

/**
 * Converts a Date object to the ISO datetime-local format expected by backend
 * APIs (`YYYY-MM-DDTHH:mm`).
 *
 * @param {Date} date - The Date object to convert.
 * @returns {string} ISO datetime-local string.
 */
export function formatDateToDB(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Legacy locale-defaulting formatter. Prefer `formatLocalizedDate` for
 * user-facing output — it reads the active i18n language automatically.
 *
 * @param {string} iso - ISO date string.
 * @param {string} [locale] - BCP-47 locale tag (defaults to `pt-BR`).
 * @returns {string} Locale-formatted date string.
 */
export const formatDate = (iso: string, locale = "pt-BR") =>
  new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

/**
 * Maps an i18next language tag to a BCP-47 locale suitable for
 * `Intl.DateTimeFormat`. Mirrors `resolveNumberLocale` in `number-format.ts`.
 *
 * @param {string} [language] - The active i18next language tag (defaults to current).
 * @returns {string} A BCP-47 locale tag.
 */
export function resolveDateLocale(language?: string): string {
  const lang = (language ?? i18n.language ?? 'pt-BR').toLowerCase();
  if (lang.startsWith('en')) return 'en-US';
  if (lang.startsWith('es')) return 'es-ES';
  return 'pt-BR';
}

/**
 * True when a date input is empty (null, undefined, or blank string).
 *
 * @param value - ISO string or Date object.
 * @returns Whether the value is empty.
 */
function isEmptyDateValue(value: string | Date | null | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Parses a non-empty date input into a Date, without validating it.
 *
 * @param value - ISO string or Date object.
 * @returns The parsed Date (may be invalid).
 */
/** A bare calendar date, with no time and no zone: `2026-10-12`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * To date.
 */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  // `new Date('2026-10-12')` is parsed as UTC midnight, which in every
  // Brazilian zone renders as the 11th. A due date, a renewal date and a
  // contract date name a day, not an instant, so a date-only string is
  // parsed in local time and keeps the day it says.
  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

/**
 * Parses a date value, returning null for empty or invalid input instead of
 * an `Invalid Date`.
 *
 * @param value - ISO string or Date object.
 * @returns The parsed Date, or null when empty/invalid.
 */
function parseDateOrNull(value: string | Date | null | undefined): Date | null {
  if (isEmptyDateValue(value)) return null;
  const date = toDate(value as string | Date);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/**
 * Formats a date value using the active i18n language. Returns an empty string
 * for empty or invalid input.
 *
 * @param {string | Date | null | undefined} value - ISO string or Date object.
 * @param {string} [language] - Override language (defaults to `i18n.language`).
 * @param {Intl.DateTimeFormatOptions} [opts] - Custom format options.
 * @returns {string} Locale-formatted date string, or `''` when input is empty/invalid.
 */
export function formatLocalizedDate(
  value: string | Date | null | undefined,
  language?: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const date = parseDateOrNull(value);
  if (!date) return '';
  const locale = resolveDateLocale(language);
  const options: Intl.DateTimeFormatOptions = opts ?? {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  return date.toLocaleDateString(locale, options);
}

/**
 * Formats a date+time value using the active i18n language. Returns an empty
 * string for empty or invalid input.
 *
 * @param {string | Date | null | undefined} value - ISO string or Date object.
 * @param {string} [language] - Override language (defaults to `i18n.language`).
 * @returns {string} Locale-formatted date+time string, or `''` when invalid.
 */
export function formatLocalizedDateTime(
  value: string | Date | null | undefined,
  language?: string,
): string {
  return formatLocalizedDate(value, language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats a time portion only using the active i18n language.
 *
 * @param {string | Date | null | undefined} value - ISO string or Date object.
 * @param {string} [language] - Override language (defaults to `i18n.language`).
 * @returns {string} Locale-formatted time string, or `''` when invalid.
 */
export function formatLocalizedTime(
  value: string | Date | null | undefined,
  language?: string,
): string {
  const date = parseDateOrNull(value);
  if (!date) return '';
  const locale = resolveDateLocale(language);
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/** Calendar-day distances that get a word instead of a date. */
const RELATIVE_DAY_KEYS: Record<number, string> = {
  0: 'dates.today',
  1: 'dates.yesterday',
  2: 'dates.beforeYesterday',
};

/**
 * Whole calendar days between two instants, counted by the local day each one
 * falls on rather than by elapsed hours: 23:50 yesterday to 00:10 today is one
 * day apart, not zero.
 *
 * @param from - The earlier instant.
 * @param to - The reference instant.
 * @returns The number of calendar days between them.
 */
function calendarDaysBetween(from: Date, to: Date): number {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(to) - startOfDay(from)) / 86_400_000);
}

/**
 * Formats a date as "hoje", "ontem" or "anteontem" when it is that recent, and
 * as a plain date otherwise.
 *
 * Recency is what the reader is actually after in a "last contact" column: a
 * date three days old still has to be decoded against today's, while the three
 * nearest days carry the answer in the word itself. Past that the word stops
 * helping ("há 9 dias" is no easier to place than the date) so the date takes
 * over.
 *
 * Future dates fall through to the date as well — a contact dated ahead is bad
 * data, and "amanhã" would dress it up as intentional.
 *
 * @param value - ISO string or Date object.
 * @param options - Optional reference instant and language override.
 * @returns The relative word, the formatted date, or `''` when empty/invalid.
 */
export function formatRelativeDay(
  value: string | Date | null | undefined,
  options?: { now?: Date; language?: string },
): string {
  const date = parseDateOrNull(value);
  if (!date) return '';
  const days = calendarDaysBetween(date, options?.now ?? new Date());
  const key = RELATIVE_DAY_KEYS[days];
  if (!key) return formatLocalizedDate(date, options?.language);
  return i18n.t(key, { ns: 'common' });
}
